export function HigherSalePrice() {
  return (
    <section className="py-20 px-4 bg-white">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <div className="inline-block bg-teal-100 text-teal-800 px-6 py-2 rounded-full font-bold text-sm mb-4">
            BENEFIT #3
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Command a Higher Sale Price
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Sellers offering financing can typically achieve 10-20% higher sale prices because they provide valuable financing alternatives to buyers
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">
              Financing Flexibility Commands Premium Pricing
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              When you offer seller financing, you're not just selling a property—you're providing a <strong>complete financing solution</strong> that many buyers cannot obtain elsewhere. This added value justifies charging a premium price, typically <strong>10-20% above market value</strong> for comparable properties sold with traditional financing.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Buyers are willing to pay more because seller financing offers them access to properties they couldn't otherwise purchase, more flexible terms than banks provide, faster closing timelines, and the ability to acquire properties that don't meet strict bank lending criteria. The convenience and accessibility of seller financing has real monetary value that translates directly into higher purchase prices.
            </p>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              Additionally, because you're earning substantial interest income over the life of the loan (often $500,000 to $1,000,000+ on larger apartment buildings), you can afford to be more flexible on price negotiations while still maximizing your total return. The combination of higher sale price plus interest income creates a <strong>significantly larger total profit</strong> compared to traditional sales.
            </p>

            <div className="bg-teal-50 border-l-4 border-teal-600 p-6 rounded-md">
              <h4 className="text-xl font-bold text-gray-900 mb-3">Pricing Example</h4>
              <p className="text-gray-700 mb-2">
                <strong>Traditional Sale Market Value:</strong> $2,000,000
              </p>
              <p className="text-gray-700 mb-2">
                <strong>Seller Financing Premium (15%):</strong> +$300,000
              </p>
              <p className="text-xl font-bold text-teal-600 mb-4">
                Seller Financing Sale Price: $2,300,000
              </p>
              <p className="text-gray-700 mb-2">
                <strong>Plus Interest Income (7% over 15 years):</strong> +$1,138,000
              </p>
              <p className="text-2xl font-bold text-gray-900">
                Total Return: $3,438,000
              </p>
              <p className="text-sm text-gray-600 mt-2">
                vs. $2,000,000 from traditional sale
              </p>
            </div>
          </div>

          <div>
            <img
              src="https://public.readdy.ai/ai/img_res/023a849d1819e0956d6b54951e5ff3e6.jpg"
              alt="Higher sale prices achieved through seller financing premium"
              className="w-full h-96 object-cover object-top rounded-lg shadow-lg mb-8"
            />

            <div className="bg-gray-50 rounded-lg p-6">
              <h4 className="text-2xl font-bold text-gray-900 mb-4">
                Why Buyers Pay More
              </h4>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <i className="ri-check-line text-2xl text-teal-600 mr-3 flex-shrink-0"></i>
                  <span className="text-gray-700">
                    <strong>Access to Financing:</strong> Many buyers cannot qualify for traditional bank loans
                  </span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-2xl text-teal-600 mr-3 flex-shrink-0"></i>
                  <span className="text-gray-700">
                    <strong>Flexible Terms:</strong> Negotiable down payments, interest rates, and loan terms
                  </span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-2xl text-teal-600 mr-3 flex-shrink-0"></i>
                  <span className="text-gray-700">
                    <strong>Speed Advantage:</strong> Close quickly before competing offers emerge
                  </span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-2xl text-teal-600 mr-3 flex-shrink-0"></i>
                  <span className="text-gray-700">
                    <strong>As-Is Purchase:</strong> Acquire properties banks won't finance due to condition
                  </span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-2xl text-teal-600 mr-3 flex-shrink-0"></i>
                  <span className="text-gray-700">
                    <strong>Lower Closing Costs:</strong> Reduced fees compared to traditional financing
                  </span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-2xl text-teal-600 mr-3 flex-shrink-0"></i>
                  <span className="text-gray-700">
                    <strong>Relationship Building:</strong> Direct negotiation with seller creates trust
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-teal-600 to-teal-700 rounded-lg p-8 md:p-12 text-white">
          <h3 className="text-3xl font-bold mb-8 text-center">Total Return Comparison</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white/10 rounded-lg p-6 backdrop-blur-sm">
              <h4 className="text-2xl font-bold mb-4">Traditional Sale</h4>
              <div className="space-y-3 text-lg">
                <div className="flex justify-between">
                  <span>Sale Price:</span>
                  <span className="font-bold">$2,000,000</span>
                </div>
                <div className="flex justify-between">
                  <span>Interest Income:</span>
                  <span className="font-bold">$0</span>
                </div>
                <div className="border-t border-white/30 pt-3 flex justify-between text-2xl">
                  <span>Total Return:</span>
                  <span className="font-bold">$2,000,000</span>
                </div>
              </div>
            </div>

            <div className="bg-white/10 rounded-lg p-6 backdrop-blur-sm border-2 border-white">
              <h4 className="text-2xl font-bold mb-4">Seller Financing</h4>
              <div className="space-y-3 text-lg">
                <div className="flex justify-between">
                  <span>Sale Price (+15%):</span>
                  <span className="font-bold">$2,300,000</span>
                </div>
                <div className="flex justify-between">
                  <span>Interest Income:</span>
                  <span className="font-bold">+$1,138,000</span>
                </div>
                <div className="border-t border-white/30 pt-3 flex justify-between text-2xl">
                  <span>Total Return:</span>
                  <span className="font-bold">$3,438,000</span>
                </div>
              </div>
              <div className="mt-4 text-center">
                <div className="text-3xl font-bold">+$1,438,000</div>
                <div className="text-teal-100">Additional Profit (72% More)</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}